import { useQuery } from '@tanstack/react-query';
import { get } from 'utils/axios';
import {
  SENSORS_STATUS_BY_CHILD,
  SENSORS_STATUS_SUMMARY,
  SENSORS_GRAPH,
  SENSORS_DATA,
  SENSORS,
  SENSORS_LIST_BY_ASSET_ID,
  SENSORS_BY_STATUS_NAME,
  GET_ALL_AVAILABLE_GRAPHS_BY_EVENT_ID,
  ALL_SENSORS_BY_ID,
  SENSORS_BY_MODEL_NAME,
  SENSORS_BY_EVENT_ID,
  SENSORS_DATA_BY_MODEL,
} from 'config/endpoints';
import { SensorsGraphProps } from 'queries/features/types';
import { TIME_FORMAT_WITH_UTC } from 'config/constants';
import { SensorFilters } from 'pages/Asset/tabs/Monitoring/components/Aside/components/Sensors/FilterSensors';
import { Attribute, AttributesList } from 'queries/attributes/contract';
import {
  SensorsCountByChild,
  SensorsCountRoot,
  SensorsData,
  SensorsGraphicData,
  SensorsList,
  SensorsStatusSummary,
} from './contract';
import {
  GenericQueryOptions,
  SensorsCountByChildProps,
  SensorsDataQueryProps,
  SensorsGraphParams,
  SensorsStatusProps,
  SensorsDataParams,
  SensorsQueryProps,
  SensorStatusList,
  GraphsPayload,
} from './types';
import { sensorsByAssetId } from './mock';

export const useGetAvaiableGraphsByEventId = (
  eventId?: string,
  startDate?: string,
  endDate?: string,
  queryOptions?: GenericQueryOptions<GraphsPayload>,
) => {
  const formattedEventId = eventId?.replace('-CLONE', '');
  return useQuery(
    ['AVAILABLE_GRAPHS_BY_EVENT_ID', eventId, startDate, endDate],

    get<GraphsPayload, { start_date: string | undefined; end_date: string | undefined }>(
      GET_ALL_AVAILABLE_GRAPHS_BY_EVENT_ID(formattedEventId),
      {
        start_date: startDate,
        end_date: endDate,
      },
    ),

    { enabled: !!eventId, ...queryOptions },
  );
};

export const useSensorsDataQuery = (
  { assetId, startDate, endDate, tags, window }: SensorsDataQueryProps,
  queryOptions?: GenericQueryOptions<SensorsData>,
  signal?: AbortSignal,
) => {
  return useQuery(
    ['SENSORS_DATA', startDate, endDate, tags, window],
    () =>
      get<SensorsData, SensorsDataParams>(SENSORS_DATA(assetId), {
        start_date: startDate?.utc().format(TIME_FORMAT_WITH_UTC),
        end_date: endDate?.utc().format(TIME_FORMAT_WITH_UTC),
        sensors: tags?.toString() ?? '',
        window: window ?? '',
        signal,
      })(),

    { ...queryOptions, retry: false, enabled: !!assetId && (queryOptions?.enabled ?? true) },
  );
};
export const useSensorsChartByModelQuery = (
  { modelName, startDate, endDate, tags, window }: SensorsDataQueryProps,
  queryOptions?: GenericQueryOptions<SensorsData>,
  signal?: AbortSignal,
) => {
  return useQuery(
    ['SENSORS_DATA_BY_MODEL', startDate, endDate, tags, window],
    () =>
      get<SensorsData, SensorsDataParams>(SENSORS_DATA_BY_MODEL(modelName), {
        start_date: startDate?.utc().format(TIME_FORMAT_WITH_UTC),
        end_date: endDate?.utc().format(TIME_FORMAT_WITH_UTC),
        tags: tags?.toString() ?? '',
        window: window ?? '',
        signal,
      })(),

    { ...queryOptions, retry: false, enabled: !!modelName && (queryOptions?.enabled ?? true) },
  );
};
export const useSensorsQuery = (
  { assetId }: SensorsQueryProps,
  queryOptions?: GenericQueryOptions<SensorsData>,
) => {
  return useQuery(
    ['SENSORS', assetId],
    get<SensorsData, SensorsDataParams>(SENSORS(assetId)),
    queryOptions,
  );
};
export const useSensorsGraphQuery = (
  { tags, startDate, endDate, window, assetId }: SensorsGraphProps,
  queryOptions?: GenericQueryOptions<SensorsGraphicData>,
) => {
  return useQuery(
    [
      'SENSORS_GRAPH_BY_ASSET_ID',
      startDate?.format('HH:mm'),
      endDate?.format('HH:mm'),
      tags,
      window,
    ],
    get<SensorsGraphicData, SensorsGraphParams>(SENSORS_GRAPH(assetId), {
      sensors: tags?.join(','),
      start_date: startDate?.utc().format(TIME_FORMAT_WITH_UTC),
      end_date: endDate?.utc().format(TIME_FORMAT_WITH_UTC),
      window,
    }),
    {
      enabled: !!tags || !!startDate || !!endDate,
      ...queryOptions,
    },
  );
};

export const useSensorStatusSummaryQuery = (
  { assetId }: SensorsStatusProps,
  queryOptions?: GenericQueryOptions<SensorsStatusSummary>,
) =>
  useQuery(
    ['SENSORS_STATUS_SUMMARY', assetId],
    get<SensorsStatusSummary>(SENSORS_STATUS_SUMMARY(assetId)),
    queryOptions,
  );

export const useSensorsCountByChildQuery = (
  { assetId }: SensorsCountByChildProps,
  queryOptions?: GenericQueryOptions<SensorsCountByChild>,
) =>
  useQuery(
    ['SENSORS_COUNT_BY_CHILD', assetId],
    get<SensorsCountByChild>(SENSORS_STATUS_BY_CHILD(assetId)),
    queryOptions,
  );

export const useSensorsCountRootQuery = (queryOptions?: GenericQueryOptions<SensorsCountRoot>) =>
  useQuery(
    ['SENSORS_COUNT_ROOT'],
    get<SensorsCountRoot>(SENSORS_STATUS_BY_CHILD(undefined)),
    queryOptions,
  );

export const useSensorsListByAssetId = (
  assetId?: string,
  filters?: { status?: string; equipmentParts?: string },
  queryOptions?: GenericQueryOptions<SensorsList>,
) =>
  useQuery(
    ['SENSORS_BY_ASSET_ID', assetId, filters?.status, filters?.equipmentParts],
    async () => {
      const response = await get<SensorsList, { status?: string; equipment_parts?: string }>(
        SENSORS_LIST_BY_ASSET_ID(assetId),
        {
          status: filters?.status !== 'ALL' ? filters?.status : undefined,
          equipment_parts: filters?.equipmentParts,
        },
      )();
      return response.sensors.length > 0 ? response : sensorsByAssetId;
    },

    { enabled: !!assetId, ...queryOptions },
  );

export const useGetAllSensorsById = (
  assetId: string,
  queryOptions?: GenericQueryOptions<SensorStatusList>,
) => {
  return useQuery(
    ['GET_ATTRIBUTTES', 'SensorStatus', assetId],
    get<SensorStatusList>(ALL_SENSORS_BY_ID(assetId)),
    queryOptions,
  );
};

export const useGetSensorsStatusById = (
  assetId: string,
  status: SensorFilters,
  queryOptions?: GenericQueryOptions<SensorStatusList>,
) => {
  const params: { status?: string } =
    status?.filter === null || status?.filter === 'ALL'
      ? { status: undefined }
      : { status: status?.filter };

  return useQuery(
    ['SensorStatus', assetId, status],
    get<SensorStatusList, { status?: string | undefined }>(SENSORS_BY_STATUS_NAME(assetId), params),
    queryOptions,
  );
};

export const useGetSensorsByModelName = (
  modelName?: string,
  queryOptions?: GenericQueryOptions<AttributesList>,
) => {
  return useQuery(
    ['SENSORS_BY_MODEL_NAME', modelName],
    async () => {
      const response = await get<{ [key: string]: Attribute[] }>(
        SENSORS_BY_MODEL_NAME(modelName),
      )();
      return { categories: response };
    },
    { enabled: !!modelName, retry: false, ...queryOptions },
  );
};
export const useGetSensorsByEventId = (
  eventId?: string,
  queryOptions?: GenericQueryOptions<AttributesList>,
) => {
  return useQuery(
    ['SENSORS_BY_EVENT_ID', eventId],
    async () => {
      const formattedEventId = eventId?.replace('-CLONE', '');
      const response = await get<{ [key: string]: Attribute[] }>(
        SENSORS_BY_EVENT_ID(formattedEventId),
      )();
      return { categories: response };
    },
    { enabled: !!eventId, retry: false, ...queryOptions },
  );
};
